// A tactic's strength bonus: the armies a player's military units form for it.
// A tactic lists the units one army needs ([infantry, cavalry, artillery]) and its value per army, e.g. "8(4)": 8 for an
// army of up-to-date units, 4 for an army with an obsolete unit (one two or more levels below the tactic). Each air
// force doubles the value of one army. The player's units are grouped into as many full-value armies as possible, then
// as many obsolete-value armies as the remaining units allow, which gives the highest total.
const TechnologyTypes = require('../Constants/TechnologyTypes');
const { AgeOrder } = require('../Constants/Constants');

const { Infantry, Cavalry, Artillery, AirForces } = TechnologyTypes.Military;
const ArmyTypes = [Infantry, Cavalry, Artillery];

// "8(4)" -> { full: 8, obsolete: 4 }; "2" -> { full: 2, obsolete: 2 }; "2 + I/II/III" (Hussars) -> { full: 2,
// obsolete: 2, plusLowestLevel: true }: each army is worth 2 plus the level of its lowest unit
const ParseValue = (text) => {
    const match = /^(\d+)(?:\((\d+)\))?/.exec(text ?? '');
    const full = match ? Number(match[1]) : 0;
    return { full, obsolete: match?.[2] !== undefined ? Number(match[2]) : full, ...(/\+ I\/II\/III/.test(text ?? '') ? { plusLowestLevel: true } : {}) };
};

// Armies for a tactic worth a base value plus the level of each army's lowest unit (Hussars): the best units go into
// armies first, which gives the highest total. units: the levels of the player's units by type.
const ArmiesByLowestLevel = (units, need, base, airForces) => {
    const pools = ArmyTypes.map((type, index) => [...units[type]].sort((a, b) => b - a).slice(0, Infinity));
    const values = [];
    for (;;)
    {
        if (ArmyTypes.some((type, index) => pools[index].length < need[index]))
        {
            break;
        }
        const members = ArmyTypes.flatMap((type, index) => pools[index].splice(0, need[index]));
        values.push(base + Math.min(...members));
    }
    // Air forces double the best armies (the first ones formed)
    const doubled = Math.min(airForces, values.length);
    const value = values.reduce((sum, v) => sum + v, 0) + values.slice(0, doubled).reduce((sum, v) => sum + v, 0);
    return { full: values.length, obsolete: 0, doubled, value };
};

// How many armies needing 'need' units of each type these counts make
const ArmiesFrom = (counts, need) => Math.min(...ArmyTypes.map((type, index) =>
    need[index] === 0 ? Infinity : Math.floor(counts[type] / need[index])));

// The armies the player's units form for a tactic card: { full, obsolete, doubled, value }.
// options: { infantryAsCavalry: infantry may stand in for cavalry (Genghis Khan), farms: how many farms may count as
// Age A infantry or artillery units (Jan Žižka) } (or just infantryAsCavalry, as true / false).
// With farms, as few as give the best total, split between the two the best way: { farms: { used, infantry, artillery },
// withoutFarms: the value the units alone would give } in the result.
const TacticArmies = (player, tacticCard, options = {}) => {
    const { infantryAsCavalry = false, farms = 0, doubleOne = false } = typeof options === 'boolean' ? { infantryAsCavalry: options } : options;
    // Marlene Dietrich: one unit counted twice, the one that gives the most ({ counted: its card's name } in the result)
    if (doubleOne)
    {
        const without = ArmiesOf(player, tacticCard, infantryAsCavalry);
        let best = { ...without, counted: null };
        player.technologies.filter(tech => ArmyTypes.includes(tech.type) && tech.GetWorkerCount() > 0).forEach(tech => {
            const again = { type: tech.type, age: tech.age, GetWorkerCount: () => 1 };
            const result = ArmiesOf({ technologies: [...player.technologies, again] }, tacticCard, infantryAsCavalry);
            if (result.value > best.value)
            {
                best = { ...result, counted: tech.name };
            }
        });
        return { ...best, withoutExtra: without.value };
    }
    if (!farms)
    {
        return ArmiesOf(player, tacticCard, infantryAsCavalry);
    }
    const FarmUnits = (type, count) => ({ type, age: AgeOrder[0], GetWorkerCount: () => count });
    const withoutFarms = ArmiesOf(player, tacticCard, infantryAsCavalry);
    let best = { ...withoutFarms, farms: { used: 0, infantry: 0, artillery: 0 } };
    for (let used = 1; used <= farms; used++)
    {
        for (let infantry = 0; infantry <= used; infantry++)
        {
            const technologies = [...player.technologies, FarmUnits(Infantry, infantry), FarmUnits(Artillery, used - infantry)];
            const result = ArmiesOf({ technologies }, tacticCard, infantryAsCavalry);
            if (result.value > best.value)
            {
                best = { ...result, farms: { used, infantry, artillery: used - infantry } };
            }
        }
    }
    return { ...best, withoutFarms: withoutFarms.value };
};

const ArmiesOf = (player, tacticCard, infantryAsCavalry) => {
    const tactic = tacticCard.tactic;
    const need = tactic.GetUnits();
    const level = AgeOrder.indexOf(tactic.GetAge());
    const { full: fullValue, obsolete: obsoleteValue, plusLowestLevel } = ParseValue(tactic.GetValueText());
    // Units by type: all of them, and those not obsolete for this tactic
    const all = {}, current = {};
    [...ArmyTypes, AirForces].forEach(type => { all[type] = 0; current[type] = 0; });
    player.technologies.filter(tech => ArmyTypes.includes(tech.type) || tech.type === AirForces).forEach(tech => {
        const workers = tech.GetWorkerCount();
        all[tech.type] += workers;
        if (AgeOrder.indexOf(tech.age) > level - 2)
        {
            current[tech.type] += workers;
        }
    });

    // With Genghis Khan, try each way of counting infantry as cavalry (up-to-date and obsolete infantry separately)
    const shifts = [];
    const maxShift = infantryAsCavalry && need[1] > 0 ? all[Infantry] : 0;
    for (let shiftAll = 0; shiftAll <= maxShift; shiftAll++)
    {
        for (let shiftCurrent = Math.max(0, shiftAll - (all[Infantry] - current[Infantry])); shiftCurrent <= Math.min(shiftAll, current[Infantry]); shiftCurrent++)
        {
            shifts.push([shiftAll, shiftCurrent]);
        }
    }
    if (shifts.length === 0)
    {
        shifts.push([0, 0]);
    }

    let best = { full: 0, obsolete: 0, doubled: 0, value: 0 };
    if (plusLowestLevel)
    {
        // Unit levels by type; with Genghis Khan, try moving each number of the best infantry over to cavalry
        const levels = {};
        ArmyTypes.forEach(type => { levels[type] = []; });
        player.technologies.filter(tech => ArmyTypes.includes(tech.type)).forEach(tech => {
            for (let i = 0; i < tech.GetWorkerCount(); i++) levels[tech.type].push(AgeOrder.indexOf(tech.age));
        });
        const infantry = [...levels[Infantry]].sort((a, b) => b - a);
        for (let moved = 0; moved <= maxShift; moved++)
        {
            const shifted = { ...levels, [Infantry]: infantry.slice(moved), [Cavalry]: [...levels[Cavalry], ...infantry.slice(0, moved)] };
            const result = ArmiesByLowestLevel(shifted, need, fullValue, all[AirForces]);
            if (result.value > best.value)
            {
                best = result;
            }
        }
        return best;
    }
    shifts.forEach(([shiftAll, shiftCurrent]) => {
        const allCounts = { ...all, [Infantry]: all[Infantry] - shiftAll, [Cavalry]: all[Cavalry] + shiftAll };
        const currentCounts = { ...current, [Infantry]: current[Infantry] - shiftCurrent, [Cavalry]: current[Cavalry] + shiftCurrent };
        const full = ArmiesFrom(currentCounts, need);
        const obsolete = ArmiesFrom(allCounts, need) - full;
        // Air forces double the best armies first
        const doubledFull = Math.min(all[AirForces], full);
        const doubledObsolete = Math.min(all[AirForces] - doubledFull, obsolete);
        const value = (full + doubledFull) * fullValue + (obsolete + doubledObsolete) * obsoleteValue;
        if (value > best.value)
        {
            best = { full, obsolete, doubled: doubledFull + doubledObsolete, value };
        }
    });
    return best;
};

module.exports = {
    TacticArmies,
    ParseValue,
};
